import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import Swal from "sweetalert2";
import { FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiArrowRight, FiCheckCircle, FiShield } from "react-icons/fi";
import config from "../config";
import AuthHeader from "./AuthHeader";
import AuthFooter from "./AuthFooter";

const SignUp = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.onmouseenter = Swal.stopTimer;
      toast.onmouseleave = Swal.resumeTimer;
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validateForm = () => {
    const errors = {};
    
    if (!name.trim()) errors.name = "Name is required";
    
    if (!email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = "Invalid email format";
    }
    
    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(password)) {
      errors.password = "Password must contain uppercase, lowercase, number, and special character";
    }
    
    return errors;
  };

  // ✅ FIXED: Google Sign In - API returns "token" field
  const handleGoogleSignUp = async (credentialResponse) => {
    setIsLoading(true);
    const decoded = jwtDecode(credentialResponse.credential);
    const { name, email, sub } = decoded;

    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/api/users/google-signin`,
        { email, gid: sub, name }
      );

      // ✅ API returns { token: "..." } - store as accessToken
      if (response.status === 200 && response.data.token) {
        localStorage.setItem("accessToken", response.data.token);
        
        Toast.fire({
          icon: "success",
          title: "Logged in successfully",
        });
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Google signup error:", error);
      Toast.fire({
        icon: "error",
        title: error.response?.data?.message || "Google signup failed",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Manual signup - does NOT store tokens (user must verify email)
  const handleManualSignUp = async (e) => {
    e.preventDefault();
    setFormErrors({});

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/api/users/signup`,
        { name, email, password, mode: "signup" }
      );

      if (response.status === 201) {
        Toast.fire({
          icon: "success",
          title: response.data.message || "Check your email to verify your account",
        });
        navigate("/login");
      }
    } catch (error) {
      console.error("Signup error:", error);
      
      const errorMessage = error.response?.data?.message || "Signup failed";
      
      Toast.fire({
        icon: "error",
        title: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign Up - Viszapp</title>
        <meta
          name="description"
          content="Create your Viszapp account and start your visa application journey today. Join thousands of satisfied users."
        />
        <meta
          name="keywords"
          content="viszapp signup, visa application signup, create account, schengen visa, UK visa, US visa"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Sign Up - Viszapp" />
        <meta
          property="og:description"
          content="Create your Viszapp account today."
        />
        <meta property="og:url" content="https://viszapp.com/signup" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-[#1C1F2E] via-[#2A3142] to-[#2A3142] flex flex-col">
        <AuthHeader />

        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-purple-500/10 rounded-full filter blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-teal-500/10 rounded-full filter blur-[100px] animate-pulse delay-1000"></div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-24 sm:px-6 lg:px-8">
          <div className="w-full max-w-md">
            {/* Welcome Section */}
            <div className="text-center mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
                Start Your Journey
              </h1>
              <p className="text-gray-400 text-base sm:text-lg">
                Create an account to begin your visa application
              </p>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center justify-center gap-6 mb-8">
              <div className="flex items-center gap-2">
                <FiShield className="text-teal-400 text-lg" />
                <span className="text-gray-400 text-sm">99% Success Rate</span>
              </div>
              <div className="flex items-center gap-2">
                <FiCheckCircle className="text-teal-400 text-lg" />
                <span className="text-gray-400 text-sm">Trusted by 2000+</span>
              </div>
            </div>

            {/* Sign Up Form Card */}
            <div className="bg-[#2F3651]/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6 sm:p-8 shadow-xl">
              {/* Google Sign Up */}
              <div className="flex justify-center mb-6">
                <GoogleLogin
                  onSuccess={handleGoogleSignUp}
                  onError={() => {
                    console.log("Google Login Failed");
                    Toast.fire({
                      icon: "error",
                      title: "Google Sign Up failed",
                    });
                  }}
                  uxMode="redirect"
                  redirectUri="https://viszapp.com/signup"
                />
              </div>

              {/* Divider */}
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#2F3651] text-gray-400">Or sign up with email</span>
                </div>
              </div>

              {/* Email/Password Form */}
              <form onSubmit={handleManualSignUp} className="space-y-5">
                {/* Name Input */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiUser className="text-gray-500" />
                    </div>
                    <input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 bg-[#252A3D] border rounded-lg
                               text-white placeholder-gray-500 focus:outline-none
                               transition-colors duration-300 ${
                                 formErrors.name
                                   ? "border-red-500 focus:border-red-500"
                                   : "border-gray-700 focus:border-teal-500"
                               }`}
                      placeholder="John Doe"
                    />
                  </div>
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-400">{formErrors.name}</p>
                  )}
                </div>

                {/* Email Input */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiMail className="text-gray-500" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 bg-[#252A3D] border rounded-lg
                               text-white placeholder-gray-500 focus:outline-none
                               transition-colors duration-300 ${
                                 formErrors.email
                                   ? "border-red-500 focus:border-red-500"
                                   : "border-gray-700 focus:border-teal-500"
                               }`}
                      placeholder="you@example.com"
                    />
                  </div>
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-400">{formErrors.email}</p>
                  )}
                </div>

                {/* Password Input */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiLock className="text-gray-500" />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`w-full pl-10 pr-12 py-3 bg-[#252A3D] border rounded-lg
                               text-white placeholder-gray-500 focus:outline-none
                               transition-colors duration-300 ${
                                 formErrors.password
                                   ? "border-red-500 focus:border-red-500"
                                   : "border-gray-700 focus:border-teal-500"
                               }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500
                               hover:text-gray-400 transition-colors"
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  {formErrors.password && (
                    <p className="mt-1 text-sm text-red-400">{formErrors.password}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Must be at least 8 characters long
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg
                           bg-gradient-to-r from-teal-500 to-teal-400 text-white font-semibold
                           hover:shadow-lg hover:shadow-teal-500/25 transition-all duration-300
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Creating account...</span>
                    </>
                  ) : (
                    <>
                      <span>Create Account</span>
                      <FiArrowRight />
                    </>
                  )}
                </button>
              </form>

              {/* Login Link */}
              <div className="mt-6 text-center">
                <p className="text-gray-400 text-sm">
                  Already have an account?{" "}
                  <Link to="/login" className="text-teal-400 hover:text-teal-300 font-medium transition-colors">
                    Login
                  </Link>
                </p>
              </div>
            </div>

            {/* Terms */}
            <p className="mt-6 text-center text-xs text-gray-500">
              By signing up, you agree to our{" "}
              <Link to="/terms" className="text-gray-400 hover:text-teal-400 transition-colors">
                Terms & Conditions
              </Link>
              , {" "}
              <Link to="/privacy" className="text-gray-400 hover:text-teal-400 transition-colors">
                Privacy Policy
              </Link>
              {" "}and{" "}
              <Link to="/service-policies" className="text-gray-400 hover:text-teal-400 transition-colors">
                Service Policies
              </Link>
            </p>
          </div>
        </div>

        <AuthFooter />
      </div>
    </>
  );
};

export default SignUp;